import type { SalesAgentAction } from "@xop/dashboard-shared";

/**
 * Turn-grouping for the Brain chat transcript.
 *
 * The agent emits one `agent_actions` row per Plan/Observe/Reflect step plus
 * `master_step` rows for the orchestrator. The flat feed reads like a log; a
 * chat reads better when a single *step* is folded into one "turn" message —
 * the agent thinks, uses a tool, sees a result, maybe decides — exactly how
 * Claude renders an assistant turn (thinking → tool_use → tool_result).
 *
 * `groupTurns` is PURE (no React, no I/O) so the folding rules are unit-tested
 * in isolation. It returns an oldest→newest list of `ChatItem`s: mission
 * dividers interleaved with turns.
 */

export type Speaker = "master" | "discovery" | "system";

export interface ToolUse {
  tool: string;
  /** The one interesting argument, already formatted (query / url / org / …). */
  arg: string;
}

export type DecisionKind =
  | "lead"
  | "stop"
  | "queue"
  | "investigate"
  | "brief"
  | "knowledge"
  | "strategy"
  | "error";

export interface Decision {
  kind: DecisionKind;
  text: string;
}

export interface Turn {
  /** Stable React key. */
  key: string;
  speaker: Speaker;
  missionId: string | null;
  missionLabel: string | null;
  stepIndex: number | null;
  /** Friendly label of the model that ACTUALLY reasoned this turn (from the
   * agent-recorded provider) — e.g. "Claude" / "OpenAI". Absent
   * for older actions that predate provider recording. */
  model?: string;
  /** The agent's reasoning for this turn (reflection / thought). */
  thinking?: string;
  /** The tool the agent invoked this turn, if any. */
  toolUse?: ToolUse;
  /** Raw observation payload (label / results / hits / persons / text / url). */
  observation?: Record<string, unknown>;
  /** A standout outcome (lead captured, stopped, mission queued, error, …). */
  decision?: Decision;
  /** How long the agent's reasoning call took (ms), if recorded — rendered as
   * "Thought for Xs", like real Claude chat. */
  latencyMs?: number;
  /** ISO timestamp of the latest action folded into this turn. */
  ts: string;
}

export type ChatItem =
  | { type: "divider"; key: string; missionId: string | null; missionLabel: string | null }
  | { type: "turn"; key: string; turn: Turn };

const DISCOVERY_KINDS = new Set(["plan", "observe", "reflect"]);

/** Friendly label for the agent-recorded `provider`, for the per-turn badge.
 * The agent records only the PROVIDER, and which model that provider runs is
 * chosen at System Admin ▸ AI Providers (any model the vendor offers), so the
 * badge names the vendor rather than guessing a model it may not be. Returns
 * undefined for an unknown/absent provider so the badge simply doesn't
 * render. */
export function modelLabel(provider: unknown): string | undefined {
  switch (String(provider ?? "")) {
    case "anthropic":
      return "Claude";
    case "openai":
      return "OpenAI";
    case "deepseek":
      return "DeepSeek";
    default:
      return undefined;
  }
}

/** Tools whose `plan`/`master_step` invocation IS a notable decision. */
function decisionFor(tool: string, args: Record<string, unknown>): Decision | undefined {
  switch (tool) {
    case "extract_org":
    case "write_lead":
      return { kind: "lead", text: str(args.name) || "a new organisation" };
    case "stop":
    case "stop_for_now":
      return { kind: "stop", text: str(args.reason) };
    case "queue_mission":
      return { kind: "queue", text: [str(args.kind), str(args.vertical)].filter(Boolean).join(" · ") };
    case "open_investigation":
      return { kind: "investigate", text: str(args.target) };
    case "write_brief":
      return { kind: "brief", text: str(args.org_lead_id) ? `lead ${str(args.org_lead_id).slice(0, 8)}…` : "" };
    default:
      return undefined;
  }
}

/**
 * Format the first interesting argument of a tool call as a short string.
 * (Mirrors the legacy ActivityFeed `describePlan`, kept here so the chat
 * module is self-contained.)
 */
export function describeArg(tool: string, args: Record<string, unknown>): string {
  if (!args) return "";
  if (
    tool === "search" || tool === "social_search" || tool === "social" ||
    tool === "news_search" || tool === "news" || tool === "web_search" ||
    tool === "kb_search"
  ) {
    const q = str(args.query);
    return q ? `"${clip(q, 160)}"` : "";
  }
  if (tool === "fetch" || tool === "fetch_url") return clip(str(args.url), 160);
  if (tool === "enrich_org") return str(args.domain);
  if (tool === "validate_email") {
    if (args.email) return str(args.email);
    if (args.name && args.domain) return `${str(args.name)} @ ${str(args.domain)}`;
    return "";
  }
  if (tool === "extract_org" || tool === "write_lead") return str(args.name);
  if (tool === "write_brief") return str(args.org_lead_id) ? `lead=${str(args.org_lead_id).slice(0, 8)}…` : "";
  if (tool === "queue_mission") return [str(args.kind), str(args.vertical)].filter(Boolean).join(" · ");
  if (tool === "open_investigation") return clip(str(args.target), 160);
  if (tool === "advance_investigation" || tool === "close_investigation") {
    return str(args.id) ? `id=${str(args.id).slice(0, 8)}…` : "";
  }
  if (tool === "stop" || tool === "stop_for_now") return str(args.reason);
  if (tool === "reflect") return clip(str(args.thought), 160);
  return "";
}

/**
 * Fold a flat action list into an oldest→newest chat transcript.
 */
export function groupTurns(actions: readonly SalesAgentAction[]): ChatItem[] {
  const sorted = [...actions].sort((a, b) => {
    const t = a.created_at.localeCompare(b.created_at);
    if (t !== 0) return t;
    return (a.step_index ?? 0) - (b.step_index ?? 0);
  });

  const items: ChatItem[] = [];
  // Accumulator for the current discovery step we're folding.
  let cur: { missionId: string | null; step: number; turn: Turn } | null = null;
  let lastMissionId: string | null | undefined; // undefined = "no divider emitted yet"

  const flush = () => {
    if (cur) {
      items.push({ type: "turn", key: cur.turn.key, turn: cur.turn });
      cur = null;
    }
  };

  const maybeDivider = (missionId: string | null, missionLabel: string | null) => {
    if (missionId !== lastMissionId) {
      lastMissionId = missionId;
      // Only show a divider for a real mission (skip the master/system "no mission" runs).
      if (missionId) {
        items.push({
          type: "divider",
          key: `div-${missionId}-${items.length}`,
          missionId,
          missionLabel,
        });
      }
    }
  };

  for (const a of sorted) {
    if (DISCOVERY_KINDS.has(a.kind)) {
      maybeDivider(a.mission_id, a.mission_label);
      const step = a.step_index ?? 0;
      // New step (or new mission) → start a fresh discovery turn.
      if (!cur || cur.missionId !== a.mission_id || cur.step !== step) {
        flush();
        cur = {
          missionId: a.mission_id,
          step,
          turn: {
            key: `turn-${a.mission_id ?? "none"}-${step}-${a.id}`,
            speaker: "discovery",
            missionId: a.mission_id,
            missionLabel: a.mission_label,
            stepIndex: step,
            ts: a.created_at,
          },
        };
      }
      foldDiscovery(cur.turn, a);
      cur.turn.ts = a.created_at;
    } else {
      // master_step / system events / errors — each is its own turn.
      flush();
      // A master turn has no mission; keep the divider state so returning to a
      // mission re-announces it.
      if (a.kind === "master_step") lastMissionId = null;
      items.push({ type: "turn", key: `turn-${a.id}`, turn: buildSpecialTurn(a) });
    }
  }
  flush();
  return items;
}

/** Merge one discovery action (plan | observe | reflect) into a turn. */
function foldDiscovery(turn: Turn, a: SalesAgentAction): void {
  if (a.kind === "plan") {
    const plan = (a.payload?.plan as Record<string, unknown> | undefined) ?? {};
    const tool = str(plan.tool) || "?";
    // The model that produced this reasoning step (recorded by the agent).
    const ml = modelLabel(a.payload?.provider);
    if (ml) turn.model = ml;
    const reflection = str(a.payload?.reflection);
    if (reflection) turn.thinking = turn.thinking ? `${turn.thinking}\n${reflection}` : reflection;
    const d = decisionFor(tool, plan);
    // A decision callout names the action ("Lead captured", "Stopped", …), so
    // it subsumes the tool chip — only show the raw tool chip for non-decision
    // tools (search / fetch / enrich / …).
    if (d) turn.decision = d;
    else turn.toolUse = { tool, arg: describeArg(tool, plan) };
  } else if (a.kind === "observe") {
    const obs = (a.payload?.observation as Record<string, unknown> | undefined) ?? {};
    // Fall back to summary so an observation without a structured payload still shows.
    turn.observation = Object.keys(obs).length ? obs : { label: a.summary };
  } else if (a.kind === "reflect") {
    const thought = a.summary;
    if (thought) turn.thinking = turn.thinking ? `${turn.thinking}\n${thought}` : thought;
  }
}

/** A master_step / self_knowledge / strategy / error / unknown action → one turn. */
function buildSpecialTurn(a: SalesAgentAction): Turn {
  const base: Turn = {
    key: `turn-${a.id}`,
    speaker: a.kind === "master_step" ? "master" : "system",
    missionId: a.mission_id,
    missionLabel: a.mission_label,
    stepIndex: a.step_index ?? null,
    ts: a.created_at,
  };

  if (a.kind === "master_step") {
    const p = a.payload ?? {};
    const tool = str(p.tool) || "?";
    const ml = modelLabel(p.provider);
    if (ml) base.model = ml;
    const lat = Number(p.latency_ms);
    if (Number.isFinite(lat) && lat > 0) base.latencyMs = lat;
    const args = (p.args as Record<string, unknown> | undefined) ?? {};
    const reflection = str(p.reflection);
    if (reflection) base.thinking = reflection;
    const resultHead = str(p.result_head);
    if (resultHead) base.observation = { text: clip(resultHead, 400) };
    const d = decisionFor(tool, args);
    // Decision callout subsumes the tool chip (see foldDiscovery).
    if (d) base.decision = d;
    else base.toolUse = { tool, arg: describeArg(tool, args) };
    return base;
  }

  if (a.kind === "tick_error") {
    base.decision = { kind: "error", text: a.summary || "tick error" };
    return base;
  }
  if (a.kind === "self_knowledge_refreshed") {
    base.decision = { kind: "knowledge", text: a.summary || "refreshed self-knowledge" };
    return base;
  }
  if (a.kind === "strategy_regenerated") {
    base.decision = { kind: "strategy", text: a.summary || "regenerated strategy" };
    return base;
  }

  // Unknown kind — show the summary as plain thinking so nothing is dropped.
  base.thinking = a.summary || a.kind.replace(/_/g, " ");
  return base;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function clip(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
